
import { useState } from "react";
import { useForm } from "react-hook-form";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { PromptTestInput, PromptTestOutput } from "@/types/admin";
import { toast } from "@/hooks/use-toast";
import { runPromptTest } from "@/services/admin";

// Sample prompt templates
const PROMPT_TEMPLATES = [
  {
    id: "default",
    name: "Default Template",
    content: `You are an assistant for a car dealership. Your goal is to help reactivate cold leads.
Given the following lead information, create a friendly script to use when calling them.
Keep it conversational and focus on their previous interest.`
  },
  {
    id: "detailed",
    name: "Detailed Template",
    content: `You are an assistant for {dealership_name}. Your goal is to help reactivate cold leads.
The lead's name is {lead_name} and they previously showed interest in {vehicle_model}.
Generate a personalized script that references their specific interests and offers them
additional information or incentives to come in for a test drive.`
  },
  {
    id: "custom",
    name: "Custom Template",
    content: ""
  }
];

const PromptTestingLab = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("script");
  const [selectedTemplate, setSelectedTemplate] = useState("default");
  const [results, setResults] = useState<PromptTestOutput | null>(null);

  const form = useForm<PromptTestInput>({
    defaultValues: {
      leadData: JSON.stringify({
        name: "John Smith",
        email: "john.smith@example.com",
        phone: "555-123-4567",
        last_contact: "2023-12-15",
        vehicle_interest: "Honda Accord 2023",
        notes: "Visited showroom once but didn't test drive"
      }, null, 2),
      promptTemplate: PROMPT_TEMPLATES[0].content,
      transcript: ""
    }
  });

  const onTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = PROMPT_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      form.setValue("promptTemplate", template.content);
    }
  };

  const onSubmit = async (data: PromptTestInput) => {
    try {
      setLoading(true);
      const result = await runPromptTest(data);
      setResults(result);
      toast({
        title: "Prompt test completed",
        description: "The AI has processed your prompt and generated results."
      });
    } catch (error) {
      console.error("Error running prompt test:", error);
      toast({
        title: "Error testing prompt",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="leadData"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lead Data (JSON)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter lead data in JSON format"
                        className="font-mono h-[200px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                <FormLabel>Prompt Template</FormLabel>
                <Select onValueChange={onTemplateChange} defaultValue={selectedTemplate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a prompt template" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROMPT_TEMPLATES.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <FormField
                control={form.control}
                name="promptTemplate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prompt Content</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter prompt template"
                        className="h-[200px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="transcript"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Call Transcript (Optional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter call transcript if simulating post-call analysis"
                        className="h-[150px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Running AI Test...
                  </>
                ) : (
                  "Run AI Test"
                )}
              </Button>
            </div>

            <div className="border rounded-md p-4 bg-muted/30">
              <h3 className="text-lg font-medium mb-4">Results</h3>
              
              {results ? (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="script">Call Script</TabsTrigger>
                    <TabsTrigger value="summary">Summary</TabsTrigger>
                    <TabsTrigger value="model">Model Output</TabsTrigger>
                  </TabsList>
                
                  <TabsContent value="script" className="mt-4 space-y-2">
                    <div className="bg-white rounded-md p-4 border">
                      <p className="whitespace-pre-wrap">{results.callScript}</p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="summary" className="mt-4 space-y-2">
                    <div className="space-y-4">
                      <div className="bg-white rounded-md p-4 border">
                        <h4 className="font-medium mb-2">Intent Summary</h4>
                        <p>{results.summary}</p>
                      </div>
                      
                      <div className="bg-white rounded-md p-4 border">
                        <h4 className="font-medium mb-2">Intent Score</h4>
                        <div className="relative h-6 w-full rounded-full bg-gray-200">
                          <div 
                            className="absolute left-0 top-0 h-full rounded-full bg-green-500"
                            style={{ width: `${results.intentScore ? results.intentScore * 100 : 0}%` }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
                            {results.intentScore ? (results.intentScore * 100).toFixed(0) : 0}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="model" className="mt-4 space-y-2">
                    <div className="bg-white rounded-md p-4 border">
                      <pre className="text-xs font-mono whitespace-pre-wrap overflow-auto max-h-[400px]">
                        {results.modelOutput}
                      </pre>
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                  Run the AI test to see results here
                </div>
              )}
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default PromptTestingLab;
